import { CardContent } from '@/components/ui/card';
import { ChatMessage, StoredFile } from '@/api/chat';
import { useEffect, useRef, useCallback, useState } from 'react';
import { Loader2, Download, FileIcon, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { chatApi } from '@/api/chat';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface MessageThreadProps {
  selectedChannelId?: string;
  messages?: ChatMessage[];
  lastReadAt?: string | null;
  onMessageVisible?: (messageId: string, timestamp: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

export function MessageThread({ 
  selectedChannelId, 
  messages, 
  lastReadAt, 
  onMessageVisible,
  onLoadMore,
  hasMore,
  isLoadingMore 
}: MessageThreadProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const hasScrolledToInitialPosition = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const [prevScrollHeight, setPrevScrollHeight] = useState(0);
  const isLoadingRef = useRef(false);
  const [fileBlobUrls, setFileBlobUrls] = useState<Map<string, string>>(new Map());
  const [expandedFile, setExpandedFile] = useState<StoredFile | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Scroll to last read position on initial load
  useEffect(() => {
    if (!selectedChannelId || !messages || messages.length === 0) {
      hasScrolledToInitialPosition.current = false;
      return;
    }

    if (!hasScrolledToInitialPosition.current && containerRef.current) {
      const scrollToPosition = () => {
        if (!lastReadAt || !containerRef.current) {
          // No read status, scroll to bottom
          containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight });
          hasScrolledToInitialPosition.current = true;
          return;
        }

        // Find the first unread message (after lastReadAt)
        const firstUnreadIndex = messages.findIndex(m => 
          new Date(m.createdAt).getTime() > new Date(lastReadAt).getTime()
        );

        if (firstUnreadIndex === -1) {
          // All messages read, scroll to bottom
          containerRef.current.scrollTo({ top: containerRef.current.scrollHeight });
        } else if (firstUnreadIndex === 0) {
          // All messages unread, scroll to top
          containerRef.current.scrollTo({ top: 0 });
        } else {
          // Scroll to first unread message
          const messageElement = messageRefs.current.get(messages[firstUnreadIndex].id);
          if (messageElement) {
            messageElement.scrollIntoView({ block: 'start' });
          }
        }
        hasScrolledToInitialPosition.current = true;
      };

      // Wait for next tick to ensure DOM is ready
      setTimeout(scrollToPosition, 50);
    }
  }, [selectedChannelId, messages, lastReadAt]);

  // Maintain scroll position when loading more messages
  useEffect(() => {
    if (prevScrollHeight > 0 && containerRef.current) {
      const newScrollHeight = containerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeight;
      containerRef.current.scrollTop += scrollDiff;
      setPrevScrollHeight(0);
    }
  }, [messages, prevScrollHeight]);

  // Reset scroll flag when channel changes
  useEffect(() => {
    hasScrolledToInitialPosition.current = false;
  }, [selectedChannelId]);

  // Set up Intersection Observer for "load more" trigger at the top
  useEffect(() => {
    if (!loadMoreTriggerRef.current || !onLoadMore || !hasMore || isLoadingMore) return;

    if (loadMoreObserverRef.current) {
      loadMoreObserverRef.current.disconnect();
    }

    loadMoreObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLoadingMore && !isLoadingRef.current && containerRef.current) {
            isLoadingRef.current = true;
            setPrevScrollHeight(containerRef.current.scrollHeight);
            onLoadMore();
          }
        });
      },
      {
        root: containerRef.current,
        threshold: 0.1,
      }
    );

    loadMoreObserverRef.current.observe(loadMoreTriggerRef.current);

    return () => {
      if (loadMoreObserverRef.current) {
        loadMoreObserverRef.current.disconnect();
      }
    };
  }, [onLoadMore, hasMore, isLoadingMore]);

  // Reset loading ref when loading completes
  useEffect(() => {
    if (!isLoadingMore) {
      isLoadingRef.current = false;
    }
  }, [isLoadingMore]);

  // Set up Intersection Observer for visibility tracking
  useEffect(() => {
    if (!selectedChannelId || !messages || !onMessageVisible) return;

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const messageId = entry.target.getAttribute('data-message-id');
            const messageTime = entry.target.getAttribute('data-message-time');
            if (messageId && messageTime && onMessageVisible) {
              onMessageVisible(messageId, messageTime);
            }
          }
        });
      },
      {
        root: containerRef.current,
        threshold: 1.0, // Message must be fully visible
      }
    );

    // Observe all message elements
    messageRefs.current.forEach((element) => {
      if (observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [selectedChannelId, messages, onMessageVisible]);

  const setMessageRef = useCallback((messageId: string) => (el: HTMLDivElement | null) => {
    if (el) {
      messageRefs.current.set(messageId, el);
    } else {
      messageRefs.current.delete(messageId);
    }
  }, []);

  const handleDownloadFile = async (fileId: string, filename: string) => {
    try {
      const blob = await chatApi.downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  };

  const handleExpandFile = (file: StoredFile) => {
    setExpandedFile(file);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setExpandedFile(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImage = (contentType: string): boolean => {
    return contentType.startsWith('image/');
  };

  const isVideo = (contentType: string): boolean => {
    return contentType.startsWith('video/');
  };

  const isAudio = (contentType: string): boolean => {
    return contentType.startsWith('audio/');
  };

  const isPDF = (contentType: string): boolean => {
    return contentType === 'application/pdf';
  };

  // Load file blobs for inline viewing
  useEffect(() => {
    if (!messages) return;

    const loadFilesForInlineViewing = async () => {
      const urlsToCreate: Map<string, string> = new Map();
      
      for (const message of messages) {
        if (message.attachment) {
          const fileId = message.attachment.id;
          const contentType = message.attachment.contentType;
          
          // Only load blobs for files that support inline viewing (including PDFs)
          if ((isImage(contentType) || isVideo(contentType) || isAudio(contentType) || isPDF(contentType)) && !fileBlobUrls.has(fileId)) {
            try {
              const blob = await chatApi.downloadFile(fileId);
              const blobUrl = URL.createObjectURL(blob);
              urlsToCreate.set(fileId, blobUrl);
            } catch (error) {
              console.error(`Failed to load file ${fileId}:`, error);
            }
          }
        }
      }
      
      // Only update state if we created new URLs
      if (urlsToCreate.size > 0) {
        setFileBlobUrls(prev => new Map([...prev, ...urlsToCreate]));
      }
    };

    loadFilesForInlineViewing();
  }, [messages]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      fileBlobUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  return (
    <CardContent ref={containerRef} className="flex-1 overflow-y-auto space-y-3">
      {!selectedChannelId && (
        <div className="text-sm text-muted-foreground">Pick a chat to see its messages.</div>
      )}
      {selectedChannelId && (!messages || messages.length === 0) && !isLoadingMore && (
        <div className="text-sm text-muted-foreground">No messages yet.</div>
      )}
      {selectedChannelId && hasMore && (
        <div ref={loadMoreTriggerRef} className="flex justify-center py-2">
          {isLoadingMore && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading older messages...
            </div>
          )}
        </div>
      )}
      {selectedChannelId && messages && messages.map((m) => {
        const messageTime = new Date(m.createdAt).toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });

        if (m.messageType === 'SYSTEM') {
          // System message - centered, no background, no user, time on right
          return (
            <div 
              key={m.id}
              ref={setMessageRef(m.id)}
              data-message-id={m.id}
              data-message-time={m.createdAt}
              className="flex justify-center items-center gap-2"
            >
              <div className="text-xs text-muted-foreground italic py-1">
                {m.content}
              </div>
              <span className="text-xs text-muted-foreground flex-shrink-0">
                {messageTime}
              </span>
            </div>
          );
        }

        // Regular user message
        return (
          <div 
            key={m.id}
            ref={setMessageRef(m.id)}
            data-message-id={m.id}
            data-message-time={m.createdAt}
            className={`flex items-end gap-2 ${m.fromCurrentUser ? 'justify-end' : 'justify-start'}`}
          >
            {!m.fromCurrentUser && (
              <span className="text-xs text-muted-foreground flex-shrink-0 pb-2">
                {messageTime}
              </span>
            )}
            <div className={`max-w-[75%] rounded-lg border px-3 py-2 text-sm ${m.fromCurrentUser ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
              <div className={`text-xs ${m.fromCurrentUser ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                {m.fromCurrentUser ? 'You' : (m.senderName || 'Unknown')}
              </div>
              <div>{m.content}</div>
              {m.attachment && (
                <div className={`mt-2 rounded border ${m.fromCurrentUser ? 'border-primary-foreground/20 bg-primary-foreground/10' : 'border-border bg-background'}`}>
                  {isImage(m.attachment.contentType) && fileBlobUrls.has(m.attachment.id) && (
                    <div className="space-y-2">
                      <img 
                        src={fileBlobUrls.get(m.attachment.id)}
                        alt={m.attachment.originalFilename}
                        className="max-w-full rounded cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => {
                          const url = fileBlobUrls.get(m.attachment!.id);
                          if (url) window.open(url, '_blank');
                        }}
                        style={{ maxHeight: '400px', objectFit: 'contain' }}
                      />
                      <div className="flex items-center gap-2 p-2">
                        <FileIcon className="h-4 w-4 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium truncate">{m.attachment.originalFilename}</div>
                          <div className={`text-xs ${m.fromCurrentUser ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                            {formatFileSize(m.attachment.sizeBytes)}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => handleExpandFile(m.attachment!)}
                          title="Expand"
                        >
                          <Maximize2 className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => handleDownloadFile(m.attachment!.id, m.attachment!.originalFilename)}
                          title="Download"
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  {isVideo(m.attachment.contentType) && fileBlobUrls.has(m.attachment.id) && (
                    <div className="space-y-2">
                      <video 
                        controls
                        className="max-w-full rounded"
                        style={{ maxHeight: '400px' }}
                      >
                        <source src={fileBlobUrls.get(m.attachment.id)} type={m.attachment.contentType} />
                        Your browser does not support the video tag.
                      </video>
                      <div className="flex items-center gap-2 p-2">
                        <FileIcon className="h-4 w-4 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium truncate">{m.attachment.originalFilename}</div>
                          <div className={`text-xs ${m.fromCurrentUser ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                            {formatFileSize(m.attachment.sizeBytes)}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => handleExpandFile(m.attachment!)}
                          title="Expand"
                        >
                          <Maximize2 className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => handleDownloadFile(m.attachment!.id, m.attachment!.originalFilename)}
                          title="Download"
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  {isAudio(m.attachment.contentType) && fileBlobUrls.has(m.attachment.id) && (
                    <div className="space-y-2 p-2">
                      <audio 
                        controls
                        className="w-full"
                      >
                        <source src={fileBlobUrls.get(m.attachment.id)} type={m.attachment.contentType} />
                        Your browser does not support the audio tag.
                      </audio>
                      <div className="flex items-center gap-2">
                        <FileIcon className="h-4 w-4 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium truncate">{m.attachment.originalFilename}</div>
                          <div className={`text-xs ${m.fromCurrentUser ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                            {formatFileSize(m.attachment.sizeBytes)}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => handleExpandFile(m.attachment!)}
                          title="Expand"
                        >
                          <Maximize2 className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => handleDownloadFile(m.attachment!.id, m.attachment!.originalFilename)}
                          title="Download"
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  {isPDF(m.attachment.contentType) && fileBlobUrls.has(m.attachment.id) && (
                    <div className="space-y-2">
                      <iframe
                        src={fileBlobUrls.get(m.attachment.id)}
                        className="w-full rounded"
                        style={{ height: '400px' }}
                        title={m.attachment.originalFilename}
                      />
                      <div className="flex items-center gap-2 p-2">
                        <FileIcon className="h-4 w-4 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium truncate">{m.attachment.originalFilename}</div>
                          <div className={`text-xs ${m.fromCurrentUser ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                            {formatFileSize(m.attachment.sizeBytes)}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => handleExpandFile(m.attachment!)}
                          title="Expand"
                        >
                          <Maximize2 className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => handleDownloadFile(m.attachment!.id, m.attachment!.originalFilename)}
                          title="Download"
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  {!isImage(m.attachment.contentType) && 
                   !isVideo(m.attachment.contentType) && 
                   !isAudio(m.attachment.contentType) && 
                   !isPDF(m.attachment.contentType) && (
                    <div className="flex items-center gap-2 p-2">
                      <FileIcon className="h-4 w-4 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{m.attachment.originalFilename}</div>
                        <div className={`text-xs ${m.fromCurrentUser ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                          {formatFileSize(m.attachment.sizeBytes)}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => handleDownloadFile(m.attachment!.id, m.attachment!.originalFilename)}
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
            {m.fromCurrentUser && (
              <span className="text-xs text-muted-foreground flex-shrink-0 pb-2">
                {messageTime}
              </span>
            )}
          </div>
        );
      })}

      {/* Fullscreen file viewer modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-base font-medium truncate pr-8">
              {expandedFile?.originalFilename}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto p-4 pt-2" style={{ maxHeight: 'calc(95vh - 80px)' }}>
            {expandedFile && (
              <div className="h-full flex items-center justify-center">
                {isImage(expandedFile.contentType) && fileBlobUrls.has(expandedFile.id) && (
                  <img
                    src={fileBlobUrls.get(expandedFile.id)}
                    alt={expandedFile.originalFilename}
                    className="max-w-full max-h-full object-contain"
                  />
                )}
                {isVideo(expandedFile.contentType) && fileBlobUrls.has(expandedFile.id) && (
                  <video
                    controls
                    className="max-w-full max-h-full"
                    autoPlay
                  >
                    <source src={fileBlobUrls.get(expandedFile.id)} type={expandedFile.contentType} />
                    Your browser does not support the video tag.
                  </video>
                )}
                {isAudio(expandedFile.contentType) && fileBlobUrls.has(expandedFile.id) && (
                  <div className="w-full max-w-2xl">
                    <audio
                      controls
                      className="w-full"
                      autoPlay
                    >
                      <source src={fileBlobUrls.get(expandedFile.id)} type={expandedFile.contentType} />
                      Your browser does not support the audio tag.
                    </audio>
                    <div className="mt-4 text-center text-sm text-muted-foreground">
                      <div className="font-medium">{expandedFile.originalFilename}</div>
                      <div className="text-xs mt-1">{formatFileSize(expandedFile.sizeBytes)}</div>
                    </div>
                  </div>
                )}
                {isPDF(expandedFile.contentType) && fileBlobUrls.has(expandedFile.id) && (
                  <iframe
                    src={fileBlobUrls.get(expandedFile.id)}
                    className="w-full h-full"
                    style={{ minHeight: '80vh' }}
                    title={expandedFile.originalFilename}
                  />
                )}
              </div>
            )}
          </div>
          <div className="p-4 pt-2 border-t flex justify-end gap-2">
            {expandedFile && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDownloadFile(expandedFile.id, expandedFile.originalFilename)}
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleCloseModal}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </CardContent>
  );
}
